const mainBreakpoints = ["mobile", "tablet", "desktop", "widescreen", "super-widescreen"];

/**
 * Gets the current breakpoint
 */
const getBreakpoint = () => {
  return window.getComputedStyle(document.body, ':before').content.replace(/\"/g, '');
}

/**
 * Create an event listener on resize.
 * When the breakpoint change it dispatch an event with details
 * about the old breakpoint and the new breakpoint
 */
 const initiateBreakpoinListener = () => {

  let breakpoint = getBreakpoint();
  window.addEventListener('resize', () => {
    const currentBreakpoint = getBreakpoint();

    if(breakpoint !== currentBreakpoint){
      var event = new CustomEvent("breakpoint", {
        detail: {
          oldBreakpoint: breakpoint,
          newBreakpoint: currentBreakpoint
        }
      });
      document.dispatchEvent(event);
    }

    breakpoint = currentBreakpoint;
  });
}

/**
 * Checks if we got out of a group of breakpoints
 * if both old and new breakpoint are part of the group it means it didn't change.
 * @param {String} breakpoints - list of breakpoints as a string
 * @param {*} oldBreakpoint - old break point
 * @param {*} newBreakpoint - new break point
 */
const breakpointChanged = (breakpoints, oldBreakpoint, newBreakpoint) => {
  const outsideGroup = mainBreakpoints.filter(item => !breakpoints.includes(item));

  return !(breakpoints.includes(oldBreakpoint) && breakpoints.includes(newBreakpoint)) &&
  !(outsideGroup.includes(oldBreakpoint) && outsideGroup.includes(newBreakpoint));
}


/**
 * Excute calbacks when breakpoints are entered and left.
 *
 * Returns an object with the function "destroy" to remove the event listener.
 *
 * @param {String} breakpoints - list of breakpoints as a string
 * @param {Function} callbackIn - triggered when breakpoints is entered
 * @param {Function} callbackOut - triggered when breakpoints is left
 */
const createBreakpointEvent = (breakpoints, callbackIn, callbackOut) => {
  const eventFunction = (e) => {
    const { oldBreakpoint, newBreakpoint } = e.detail;
    if(breakpointChanged(breakpoints, oldBreakpoint, newBreakpoint)) {
      //if we entered the breakpoint
      if(breakpoints.includes(newBreakpoint)){
        if(callbackIn) {
          callbackIn();
        }
      }
      else{
        if(callbackOut) {
          callbackOut();
        }
      }
    }
  }

  if(breakpoints.includes(getBreakpoint())) {
    callbackIn();
  }

  document.addEventListener('breakpoint', eventFunction);

  return {
    destroy: () => {
      document.removeEventListener('breakpoint', eventFunction);
    }
  }
}

/**
 * Excute calbacks when breakpoint rules are entered or left
 *
 * @param {Object} breakpointRules - the rules of the breakpoint to listen to
 * @param {string} breakpointRules.from - At which breakpoint to start (including the breakpoint)
 * @param {string} breakpointRules.until - At which breakpoint to stop (excluding the breakpoint)
 * @param {*} callbackIn - triggered when the rule is met
 * @param {*} callbackOut - triggered when the rule becomes false
 */
const onBreakpoint = (breakpointRules, callbackIn, callbackOut) => {
  const {from, until} = breakpointRules;
  const breakpoints = [];

  let addBreakpoint = false;

  //if only until is present we need to add the breakpoints from the start
  if(!from && until) {
    addBreakpoint = true;
  }

  for(const index in mainBreakpoints) {
    const breakpoint = mainBreakpoints[index];

    if (breakpoint === until) {
      break;
    }

    if(breakpoint === from || addBreakpoint) {
      addBreakpoint = true;
      breakpoints.push(breakpoint);
    }
  }

  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when breakpoint rules are entered or left
 *
 * @param {Object} breakpointRules - the rules of the breakpoint to listen to
 * @param {string} breakpointRules.from - At which breakpoint to start (including the breakpoint)
 * @param {string} breakpointRules.until - At which breakpoint to stop (excluding the breakpoint)
 */
 const isBreakpoint = (breakpointRules) => {
  const {from, until} = breakpointRules;
  const breakpoints = [];
  const currentBreakpoint = getBreakpoint();

  let addBreakpoint = false;
  if(until) {
    addBreakpoint = true;
  }

  for(const index in mainBreakpoints) {
    const breakpoint = mainBreakpoints[index];

    if (breakpoint === until) {
      break;
    }

    if(breakpoint === from || addBreakpoint) {
      addBreakpoint = true;
      breakpoints.push(breakpoint);
    }
  }

  return breakpoints.includes(currentBreakpoint);
}

export {
  getBreakpoint,
  initiateBreakpoinListener,
  onBreakpoint,
  isBreakpoint
}
